import React, { useState, useContext } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Lock, ArrowLeft, CheckCircle, Shield, HelpCircle, LifeBuoy } from 'lucide-react';

const ForgotPassword = () => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  
  // Step tracking: 'email' -> 'questions' -> 'reset' -> 'success'
  const [step, setStep] = useState(resetToken ? 'reset' : 'email');
  
  // Email step state
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Security questions state
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({ answer1: '', answer2: '', answer3: '' });
  const [verificationToken, setVerificationToken] = useState('');
  
  // Reset password state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // "Message an admin" fallback (for people who can't recover their own account).
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpName, setHelpName] = useState('');
  const [helpEmail, setHelpEmail] = useState('');
  const [helpReason, setHelpReason] = useState('');
  const [helpSending, setHelpSending] = useState(false);

  const openHelp = () => {
    setHelpEmail(email || '');
    setHelpName('');
    setHelpReason('');
    setHelpOpen(true);
  };

  const submitHelp = async (e) => {
    e.preventDefault();
    if (!helpName.trim() || !helpEmail.trim()) {
      toast.error('Please share your name and email so an admin can reach you.');
      return;
    }
    setHelpSending(true);
    try {
      await axios.post(`${API}/auth/request-login-help`, {
        name: helpName.trim(),
        email: helpEmail.trim(),
        reason: helpReason.trim(),
      });
      toast.success("An admin has been notified. They'll reach out to you shortly.");
      setHelpOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send message. Please try again.');
    } finally {
      setHelpSending(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      // Try to get security questions for this email
      const response = await axios.get(`${API}/auth/security-questions/for-reset?email=${encodeURIComponent(email)}`);
      setQuestions(response.data.questions);
      setStep('questions');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('No security questions set up for this account.');
        // No questions → they can't self-recover. Open the "Message an Admin" modal.
        openHelp();
      } else {
        toast.error(error.response?.data?.detail || 'Failed to retrieve security questions');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuestionsSubmit = async (e) => {
    e.preventDefault();
    
    if (!answers.answer1 || !answers.answer2 || !answers.answer3) {
      toast.error('Please answer all questions');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/security-questions/verify`, {
        email,
        ...answers
      });
      
      setVerificationToken(response.data.verification_token);
      setStep('reset');
      toast.success('Security questions verified!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Incorrect answers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Use token from URL if available, otherwise use verification token from security questions
      if (resetToken) {
        await axios.post(`${API}/auth/reset-password`, {
          token: resetToken,
          new_password: newPassword
        });
      } else {
        await axios.post(`${API}/auth/reset-password-with-security`, {
          email,
          new_password: newPassword,
          verification_token: verificationToken
        });
      }
      
      setStep('success');
      toast.success('Password reset successful!');
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  // Success screen
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-xl" data-testid="reset-success-card">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-heading font-bold text-[#2B6B7F] mb-2">Password Reset!</h2>
            <p className="text-slate-600 mb-4">Your password has been successfully reset.</p>
            <p className="text-sm text-slate-500">Redirecting to login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset password form (step 3 or direct link with token)
  if (step === 'reset') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-xl" data-testid="reset-password-card">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-[#2B6B7F]" />
            </div>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F]">Create New Password</CardTitle>
            <CardDescription>Enter your new password below</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="New Password (min 6 characters)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                  className="h-11"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Confirm New Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                disabled={isLoading}
                data-testid="reset-password-button"
              >
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <Link 
                to="/" 
                className="text-sm text-[#2B6B7F] hover:text-[#1E4D5F] flex items-center justify-center gap-1"
                data-testid="back-to-login-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Security questions form (step 2)
  if (step === 'questions') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-md shadow-xl" data-testid="security-questions-card">
          <CardHeader className="text-center">
            <div className="w-14 h-14 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-[#2B6B7F]" />
            </div>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F]">Security Questions</CardTitle>
            <CardDescription>
              Answer your security questions to verify your identity
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleQuestionsSubmit} className="space-y-5">
              {questions.map((question, index) => (
                <div key={index} className="space-y-2">
                  <label className="text-sm font-medium text-slate-700 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 mt-0.5 text-[#5FA8C1] flex-shrink-0" />
                    {question}
                  </label>
                  <Input
                    type="text"
                    placeholder="Your answer..."
                    value={answers[`answer${index + 1}`]}
                    onChange={(e) => setAnswers({
                      ...answers,
                      [`answer${index + 1}`]: e.target.value
                    })}
                    required
                    data-testid={`security-answer-${index + 1}`}
                    className="h-11"
                  />
                </div>
              ))}
              
              <Button
                type="submit"
                className="w-full h-11 bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                disabled={isLoading}
                data-testid="verify-answers-button"
              >
                {isLoading ? 'Verifying...' : 'Verify Answers'}
              </Button>
            </form>
            
            <div className="mt-6 text-center space-y-2">
              <button
                type="button"
                onClick={openHelp}
                className="text-sm text-teal-700 hover:text-teal-900 flex items-center justify-center gap-1 mx-auto"
                data-testid="stuck-message-admin-questions"
              >
                <LifeBuoy className="w-4 h-4" />
                Can&apos;t remember? Message an Admin
              </button>
              <button
                onClick={() => { setStep('email'); setAnswers({ answer1: '', answer2: '', answer3: '' }); }}
                className="text-sm text-[#2B6B7F] hover:text-[#1E4D5F] flex items-center justify-center gap-1 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                Try a different email
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Email input form (step 1)
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md shadow-xl" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <img 
              src="/wfmc-logo.png" 
              alt="WFMC Logo" 
              className="h-20 w-auto"
            />
          </div>
          <div className="w-14 h-14 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-7 h-7 text-[#2B6B7F]" />
          </div>
          <CardTitle className="text-2xl font-heading text-[#2B6B7F]">Forgot Password?</CardTitle>
          <CardDescription>
            Enter your email address to reset your password using your security questions.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                placeholder="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="forgot-email-input"
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-11 bg-[#2B6B7F] hover:bg-[#1E4D5F]"
              disabled={isLoading}
              data-testid="continue-button"
            >
              {isLoading ? 'Loading...' : 'Continue'}
            </Button>
          </form>
          
          <div className="mt-6 text-center space-y-3">
            <button
              type="button"
              onClick={openHelp}
              className="text-sm text-teal-700 hover:text-teal-900 flex items-center justify-center gap-1 mx-auto"
              data-testid="stuck-message-admin-email"
            >
              <LifeBuoy className="w-4 h-4" />
              Still stuck? Message an Admin
            </button>
            <Link
              to="/"
              className="text-sm text-[#2B6B7F] hover:text-[#1E4D5F] flex items-center justify-center gap-1"
              data-testid="back-to-login-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Message an Admin — fallback when the user can't self-recover */}
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent data-testid="message-admin-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#2B6B7F] flex items-center gap-2">
              <LifeBuoy className="w-5 h-5" />
              Message an Admin
            </DialogTitle>
            <DialogDescription>
              We&apos;ll send an admin your name and email so they can reset your account and reach out to you directly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitHelp} className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Your name</label>
              <Input
                type="text"
                value={helpName}
                onChange={(e) => setHelpName(e.target.value)}
                placeholder="First and last name"
                required
                data-testid="help-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Your email</label>
              <Input
                type="email"
                value={helpEmail}
                onChange={(e) => setHelpEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="help-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                What&apos;s going on? <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <Textarea
                value={helpReason}
                onChange={(e) => setHelpReason(e.target.value)}
                placeholder="e.g. I don't remember my security answers, or I never set them up."
                rows={3}
                maxLength={1000}
                data-testid="help-reason-input"
              />
            </div>
            <Button
              type="submit"
              disabled={helpSending}
              className="w-full bg-[#2B6B7F] hover:bg-[#1E4D5F]"
              data-testid="help-submit-btn"
            >
              {helpSending ? 'Sending…' : 'Notify Admin'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ForgotPassword;
